import React, { useMemo, useState, useEffect } from 'react';
import { Box, Grid, TextField, MenuItem } from '@mui/material';
import momentHijri from 'moment-hijri';

/*
 * Simple dropdown-based picker for Lunar-Hijri (Qamari) calendar.
 * Emits value back to parent as ISO YYYY-MM-DD (Gregorian) through
 *   onChange({ target: { name, value } })
 */
const MONTH_NAMES = [
  'محرم', 'صفر', 'ربیع‌الاول', 'ربیع‌الثانی', 'جمادی‌الاول', 'جمادی‌الثانی',
  'رجب', 'شعبان', 'رمضان', 'شوال', 'ذوالقعده', 'ذوالحجه'
];

const MIN_YEAR = 1356; // adapter lower bound (≈ 1937)
const MAX_YEAR = 1499; // adapter upper bound (≈ 2076)

const toIso = (y, m, d) => {
  // Build Hijri moment then convert to gregorian ISO
  try {
    const mnt = momentHijri()
      .iYear(y)
      .iMonth(m)
      .iDate(d)
      .startOf('day');
    return mnt.clone().locale('en').format('YYYY-MM-DD');
  } catch {
    return '';
  }
};

const HijriDateDropdown = ({ value, onChange, label, isRtl = false, name }) => {
  // Parse incoming value (ISO) to hijri components
  const initial = useMemo(() => {
    if (value) {
      const m = momentHijri(value, 'YYYY-MM-DD', true);
      if (m.isValid()) {
        const parsedY = m.iYear();
        const parsedM = m.iMonth();
        const parsedD = m.iDate();

        return {
          y: Number.isFinite(parsedY) ? Math.min(Math.max(parsedY, MIN_YEAR), MAX_YEAR) : momentHijri().iYear(),
          m: Number.isFinite(parsedM) ? parsedM : 0,
          d: Number.isFinite(parsedD) ? parsedD : 1,
        };
      }
    }
    // Return null values when no value is provided - don't auto-set today's date
    return {
      y: null,
      m: null,
      d: null,
    };
  }, [value]);

  const [year, setYear] = useState(initial.y);
  const [month, setMonth] = useState(initial.m);
  const [day, setDay] = useState(initial.d);

  // Sync state when initial value changes (e.g., when document data loads)
  useEffect(() => {
    setYear(initial.y);
    setMonth(initial.m);
    setDay(initial.d);
  }, [initial.y, initial.m, initial.d]);

  // recompute days in month whenever year/month changes
  const daysInMonth = useMemo(() => {
    let dim = 30;
    try {
      dim = momentHijri()
        .iYear(year)
        .iMonth(month)
        .iDaysInMonth();
    } catch {
      // ignore
    }
    // Ensure we always return a positive integer between 29-30
    if (!Number.isFinite(dim) || dim < 1) dim = 30;
    return dim;
  }, [year, month]);

  // clamp day if overflow
  useEffect(() => {
    if (day > daysInMonth) {
      setDay(daysInMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysInMonth]);

  // Notify parent whenever y/m/d change
  useEffect(() => {
    // Only emit onChange if all values are set (not null)
    if (year !== null && month !== null && day !== null) {
      const iso = toIso(year, month, day);
      if (typeof onChange === 'function') {
        onChange({ target: { name, value: iso } });
      }
    } else if (year === null && month === null && day === null) {
      // Emit empty value when all are null
      if (typeof onChange === 'function') {
        onChange({ target: { name, value: '' } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, day]);

  const years = useMemo(() => {
    const ys = [];
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) ys.push(y);
    return ys;
  }, []);

  // Ensure controlled values are never invalid to prevent MUI warnings
  const safeDay = Number.isFinite(day) && day >= 1 ? day : '';
  const safeMonth = Number.isFinite(month) && month >= 0 ? month : '';
  const safeYear = Number.isFinite(year) ? year : '';

  return (
    <Box
      dir={isRtl ? 'rtl' : 'ltr'}
      sx={{
        width: '100%',
        position: 'relative',
        pt: 2.5, /* space for floating label */
      }}
    >
      {label && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            [isRtl ? 'right' : 'left']: 14,
            fontSize: '0.75rem',
            color: 'text.secondary',
            bgcolor: 'background.paper',
            px: 0.5,
            transform: 'translateY(-50%)',
            pointerEvents: 'none',
          }}
        >
          {label}
        </Box>
      )}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={4}>
          <TextField select fullWidth size="small" value={safeDay} onChange={e => setDay(e.target.value === '' ? null : Number(e.target.value))}>
            <MenuItem value="">--</MenuItem>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(dVal => (
              <MenuItem key={dVal} value={dVal}>{dVal}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField select fullWidth size="small" value={safeMonth} onChange={e => setMonth(e.target.value === '' ? null : Number(e.target.value))}>
            <MenuItem value="">--</MenuItem>
            {MONTH_NAMES.map((mName, idx) => (
              <MenuItem key={idx} value={idx}>{mName}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField select fullWidth size="small" value={safeYear} onChange={e => setYear(e.target.value === '' ? null : Number(e.target.value))}>
            <MenuItem value="">--</MenuItem>
            {years.map(yVal => (
              <MenuItem key={yVal} value={yVal}>{yVal}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HijriDateDropdown; 